// Single shared site header — used on every page. Desktop shows the full nav (links + search +
// account + theme). Below `md` it collapses to a brand + icon actions (search / publish / theme /
// hamburger); the hamburger opens a mobile menu, and the search icon drops an input below the header.
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import AccountNav from "@/components/AccountNav";
import Logo from "@/components/Logo";
import NavLinks from "@/components/NavLinks";
// import NavSearch from "@/components/NavSearch"; // navbar search temporarily disabled
import { WORKSPACE_LINKS } from "@/lib/dashboardNav";
import { useAuth } from "@/lib/auth-context";
import { useTheme } from "@/lib/useTheme";

function SunIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4.5" />
      <path d="M12 2.5v2.5M12 19v2.5M4.9 4.9l1.8 1.8M17.3 17.3l1.8 1.8M2.5 12H5M19 12h2.5M4.9 19.1l1.8-1.8M17.3 6.7l1.8-1.8" />
    </svg>
  );
}
function MoonIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.5 14.5a8.5 8.5 0 1 1-9-11 7 7 0 0 0 9 11z" />
      <path d="M17 3.5l.7 1.5 1.5.7-1.5.7-.7 1.5-.7-1.5-1.5-.7 1.5-.7z" />
    </svg>
  );
}

const ICON_BTN =
  "flex h-[38px] w-[38px] shrink-0 appearance-none items-center justify-center rounded-md outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary/40";
const ICON_STYLE: React.CSSProperties = { background: "var(--glass)", border: "1px solid var(--border-light)", color: "var(--text-muted)" };

export default function SiteHeader() {
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [dashOpen, setDashOpen] = useState(false);
  const [q, setQ] = useState("");

  // Close the mobile menu / search on ANY navigation — including dashboard section links that only
  // change the query string (?section=teams), so the panel dismisses when a section is tapped.
  useEffect(() => { setMenuOpen(false); setSearchOpen(false); }, [pathname, search]);
  // Reset the Dashboard accordion whenever the menu closes, so it reopens collapsed.
  useEffect(() => { if (!menuOpen) setDashOpen(false); }, [menuOpen]);

  // Close the whole menu — attached to every link so a tap dismisses it even when the target URL
  // matches the current one (where the location effect above wouldn't fire).
  const closeMenu = () => setMenuOpen(false);
  // Lock body scroll while the mobile menu is open.
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [menuOpen]);

  const mLink = "block rounded-lg px-3 py-2.5 font-display text-[15px] text-text/80 transition hover:bg-white/5 hover:text-text";

  function onMobileSearch(e: React.FormEvent) {
    e.preventDefault();
    const term = q.trim();
    navigate(`/skills${term ? `?q=${encodeURIComponent(term)}` : ""}`);
    setSearchOpen(false);
  }

  return (
    <header
      className="sticky top-0 z-50 flex items-center justify-between px-5 md:px-8"
      style={{ position: "sticky", height: 72, background: "var(--bg-elev)", borderBottom: "1px solid var(--border)" }}
    >
      {/* Signature orange accent line at the very top */}
      <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-[1.5px]" style={{ background: "linear-gradient(90deg, transparent, rgba(225,100,46,0.4), transparent)" }} />

      {/* Left: brand + nav links (nav hidden on mobile) */}
      <div className="flex min-w-0 items-center gap-8 md:gap-11">
        <Link to="/" className="shrink-0 transition hover:opacity-80" style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 500, fontSize: 20 }}>
          <Logo />
        </Link>
        <div className="hidden md:block"><NavLinks /></div>
      </div>

      {/* Right (desktop): account + theme (search temporarily disabled) */}
      <div className="hidden items-center md:flex" style={{ gap: 16 }}>
        {/* <NavSearch /> */}
        <AccountNav />
        <button onClick={toggleTheme} className={ICON_BTN} style={ICON_STYLE} aria-label="Toggle theme">
          {theme === "dark" ? <SunIcon /> : <MoonIcon />}
        </button>
      </div>

      {/* Right (mobile): publish (signed in) · theme · hamburger (search temporarily disabled) */}
      <div className="flex items-center gap-1.5 md:hidden">
        {/* <button onClick={() => { setSearchOpen((o) => !o); setMenuOpen(false); }} className={ICON_BTN} style={ICON_STYLE} aria-label="Search">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
        </button> */}
        {user && (
          <Link to="/skills/new" className={ICON_BTN} style={{ ...ICON_STYLE, background: "var(--brand)", borderColor: "var(--brand)", color: "#fff" }} aria-label="Publish a skill" title="Publish a skill">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
          </Link>
        )}
        <button onClick={toggleTheme} className={ICON_BTN} style={ICON_STYLE} aria-label="Toggle theme">
          {theme === "dark" ? <SunIcon /> : <MoonIcon />}
        </button>
        <button onClick={() => { setMenuOpen((o) => !o); setSearchOpen(false); }} className={ICON_BTN} style={ICON_STYLE} aria-label={menuOpen ? "Close menu" : "Open menu"} aria-expanded={menuOpen}>
          {menuOpen ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M3 12h18M3 18h18" /></svg>
          )}
        </button>
      </div>

      {/* Search input — drops below the header when the search icon is tapped */}
      {searchOpen && (
        <form onSubmit={onMobileSearch} className="fixed inset-x-0 top-[72px] z-50 flex items-center gap-2 px-4 py-3 md:hidden" style={{ background: "var(--bg-elev)", borderBottom: "1px solid var(--border)" }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" className="shrink-0 text-text/40"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search skills & people…" className="w-full bg-transparent text-sm outline-none placeholder:text-text/40" />
          <button type="button" onClick={() => setSearchOpen(false)} aria-label="Close search" className="shrink-0 text-text/40">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
        </form>
      )}

      {/* Mobile menu panel */}
      {menuOpen && (
        <>
          <div className="fixed inset-x-0 top-[72px] bottom-0 z-40 bg-black/40 md:hidden" onClick={() => setMenuOpen(false)} />
          <nav className="fixed inset-x-0 top-[72px] z-50 max-h-[calc(100vh-72px)] overflow-y-auto px-4 py-4 md:hidden" style={{ background: "var(--bg-elev)", borderBottom: "1px solid var(--border)" }}>
            <div className="flex flex-col">
              {user && <Link to={`/u/${user.username}`} onClick={closeMenu} className={mLink}>Profile</Link>}
              {user && <Link to="/home" onClick={closeMenu} className={mLink}>Home</Link>}
              <Link to="/skills" onClick={closeMenu} className={mLink}>Explore</Link>

              {/* Workspace — a non-clickable collapsible group; its sections (incl. Dashboard) are
                  revealed by the chevron. The heading itself only toggles, it doesn't navigate. */}
              {user && (
                <>
                  <button onClick={() => setDashOpen((o) => !o)} className={`${mLink} flex w-full items-center justify-between`} aria-expanded={dashOpen}>
                    Workspace
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`shrink-0 text-text/45 transition-transform ${dashOpen ? "rotate-180" : ""}`}><path d="m6 9 6 6 6-6" /></svg>
                  </button>
                  {dashOpen && (
                    <div className="ml-2 border-l pl-2" style={{ borderColor: "var(--border)" }}>
                      {/* Home is surfaced as a top-level link above Explore, so it's excluded here. */}
                      {WORKSPACE_LINKS.filter((item) => item.to !== "/home").map((item) => (
                        <Link key={item.label} to={item.to} onClick={closeMenu} className={mLink}>
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              )}

              <Link to="/docs" onClick={closeMenu} className={mLink}>Docs</Link>
              <Link to="/contact" onClick={closeMenu} className={mLink}>Contact</Link>

              {user ? (
                <>
                  <Link to="/settings" onClick={closeMenu} className={mLink}>Settings</Link>
                  <button onClick={() => { logout(); setMenuOpen(false); }} className={`${mLink} w-full text-left text-red-500`}>Log out</button>
                </>
              ) : (
                <div className="mt-2 flex gap-3 border-t px-1 pt-3" style={{ borderColor: "var(--border)" }}>
                  <Link to="/login" onClick={closeMenu} className="flex-1 rounded-xl border px-4 py-2.5 text-center font-display text-sm font-medium text-text/80" style={{ borderColor: "var(--border-light)" }}>Log in</Link>
                  <Link to="/register" onClick={closeMenu} className="flex-1 rounded-xl bg-primary px-4 py-2.5 text-center font-display text-sm font-semibold text-white">Sign up</Link>
                </div>
              )}
            </div>
          </nav>
        </>
      )}
    </header>
  );
}
